/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { HighResolutionText } from '@appCanvasCommon/text/HighResolutionText';
import { cssVar } from '@touchplan/css-var';
import { PullColumn, Ticket } from '@touchplan/common';
import { FancySprite } from '@appCanvasCommon/graphics/FancySprite';
import { Texture, utils } from 'pixi.js-legacy';
import { TICKET_TEXTURE_OPTIONS } from '@appPlanApplication/tickets/TICKET_TEXTURE_OPTIONS';
import { FancyTextStyle } from '@appCanvasCommon/interfaces/fancy-text-style.interface';
import { FancyDefaultStyles } from '@appCanvasCommon/text/FancyDefaultStyles';
import { TimelineDisplayItem } from '@appPlanApplication/timeline/tickets/TimelineDisplayItem';
import { PromisePeriod } from '../../../app-data/models/PromisePeriod';
import { IPromisePeriodSpatialData } from '@appPlanApplication/timeline/promise-period/IPromisePeriodSpatialData';
/**
 * Visual configuration settings for the display lines.
 * These settings control the appearance of both the line and its label.
 */
const settings = {
	lineWidth: 20,
	fontColor: cssVar.black,
	fontSize: 12,
	topLabelPadding: 78, // 38 new timeline 40 padding from old timeline
	labelRotation: 90, // Text label rotation, in degrees.
	labelOrigin: 0.5, // The origin/anchor of the label text.
	iconWidth: 11, // Copied from the old DOM CSS class. No clue why it is 11px wide.
	iconHeight: 7, // Copied from the old DOM CSS class. No clue why it is 7px tall.
	constraintTimelineTicketWidth: 20, // Copied from the old DOM CSS class. No clue why it is 20px wide.
	constraintTimelineTicketHeight: 21, // Copied from the old DOM CSS class. No clue why it is 21px tall.
	milestoneTimelineTicketWidth: 20, // Copied from the old DOM CSS class. No clue why it is 20px wide.
	milestoneTimelineTicketHeight: 21, // Copied from the old DOM CSS class. No clue why it is 21px tall.
};
/**
 * Helper function to apply consistent label styling to the display line text.
 * @param label
 */
export const applyDisplayLineTextStyling = (label: HighResolutionText): void => {
	label.position.set(settings.lineWidth / 2, settings.topLabelPadding);
	label.angle = settings.labelRotation;
	label.style.fill = settings.fontColor;
	label.style.fontSize = settings.fontSize;
	label.style.wordWrap = false;
	label.anchor.set(0, settings.labelOrigin);
	label.enableTrimText = false;
};

/**
 * Helper method to get the Pull Column start X coordinate
 * Pull columns are saved in reverse order in Firebase, so we read the last item in the list to determine
 * our starting X position in world space.
 * @param columns
 * @private
 */
export const getPullSpaceStart = (columns: PullColumn[]): number => {
	// We are using the finishX for "start" because the `startX` position of the first fbdb generated column
	// does not align with the active line, leaving an arbitrary "dead space" between the active line and the first pull column startX.
	// So by aligning our container to the finishX as the "start", it allows us to skip calculating the active line/1st pull offset
	// for pull column draw positions (Container is positioned relative to the screen, but the DRAWs are relative to the container)
	return columns[columns.length - 1].finishX;
};
/**
 * Indicator Arrow Factory acts as our FancySprite creator for the orange triangle indicator.
 * This method is intended to be passed into a SpritePool object to create new sprites as part of a sprite pool.
 */
export const indicatorArrowFactory = (): FancySprite => {
	const sprite: FancySprite = new FancySprite(
		utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_TIMELINE_INDICATOR_ARROW]
	);
	sprite.anchor.set(0.5, 0.5);
	sprite.visible = true;
	sprite.width = settings.iconWidth;
	sprite.height = settings.iconHeight;
	return sprite;
};

/**
 * Timeline Text Pool Factory acts as our HighResolutionText creator for timeline text
 * This method is intended to be passed into a TextPool object to create new text objects as part of a text pool.
 */
export const timelineTextPoolFactory = (): HighResolutionText => {
	const text: HighResolutionText = new HighResolutionText(
		``,
		new FancyTextStyle(FancyDefaultStyles.DEFAULT_STYLE)
	);
	text.active = true;
	text.visible = true;
	text.enableTrimText = false;
	text.anchor.set(1, 0);
	return text;
};
/**
 * Timeline Text Pool Factory acts as our HighResolutionText creator for timeline text (active space)
 * This method is intended to be passed into a TextPool object to create new text objects as part of a text pool.
 */
export const timelineActiveTextPoolFactory = (): HighResolutionText => {
	const text: HighResolutionText = new HighResolutionText(
		``,
		new FancyTextStyle(FancyDefaultStyles.DEFAULT_STYLE)
	);
	text.active = true;
	text.visible = true;
	text.enableTrimText = false;
	text.anchor.set(0.5, 0);
	return text;
};

/**
 * Timeline Ticket Constraints Factors acts as our FancySprite creator for the grouped timeline ticket visual.
 * This method is intended to be passed into a SpritePool object to create new sprites as part of a sprite pool.
 */
export const constraintTimelineTicketFactory = (): FancySprite => {
	const sprite: FancySprite = new FancySprite(
		utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_CONSTRAINT_NAME]
	);
	sprite.anchor.set(0.5, 0.5);
	sprite.visible = true;
	sprite.width = settings.constraintTimelineTicketWidth;
	sprite.height = settings.constraintTimelineTicketHeight;
	return sprite;
};

/**
 * Timeline Ticket Constraints Factors acts as our FancySprite creator for the grouped timeline ticket visual.
 * This method is intended to be passed into a SpritePool object to create new sprites as part of a sprite pool.
 */
export const milestoneTimelineTicketFactory = (): FancySprite => {
	const sprite: FancySprite = new FancySprite(
		utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_MILESTONE_NAME]
	);
	sprite.anchor.set(0.5, 0.5);
	sprite.visible = true;
	sprite.width = settings.milestoneTimelineTicketWidth;
	sprite.height = settings.milestoneTimelineTicketHeight;
	return sprite;
};

/**
 * Interface for connecting Timeline Ticker overflow UI with the canvas/ticket data
 */
export interface ITimelineTicketDisplayItem {
	tickets: Ticket[];
}

/**
 * Timeline Ticket Display Item Factory creates new `TimelineTicketDisplayItem` when pooled objects become exhausted
 * It handles both the grouped timeline ticket visual and the individual milestone visual
 */
export const timelineTicketDisplayItemFactory = (): TimelineDisplayItem<ITimelineTicketDisplayItem> => {
	const item: TimelineDisplayItem<ITimelineTicketDisplayItem> =
		new TimelineDisplayItem<ITimelineTicketDisplayItem>();
	item.visible = true;
	return item;
};

/**
 * Interface for connecting a promise period data object with a PIXI view model in the timeline
 */
export interface ITimelinePromisePeriod {
	period: PromisePeriod;
	spatialData: IPromisePeriodSpatialData;
	dragDirection?: PeriodDragDirection;
}

/**
 * Enum containing all of the valid drag directions for the Promise Period drag handles
 */
export enum PeriodDragDirection {
	Left,
	Right,
}
/**
 * Pool factory for a 1px white texture
 */
export const promisePeriodLineFactory = (): TimelineDisplayItem<ITimelinePromisePeriod> => {
	const item: TimelineDisplayItem<ITimelinePromisePeriod> = new TimelineDisplayItem<ITimelinePromisePeriod>();
	item.visible = true;
	return item;
};
